import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useIsCompact } from '@/hooks/useLayoutMode'
import { ImageLightbox } from './ImageLightbox'
import { useAppStore } from '@/store/appStore'
import { injectGlossarySpans } from '@/utils/glossaryInjector'
import { useOfflineRichContentMediaUrls, resolveOriginalKey } from '@/hooks/useOfflineRichContentMediaUrls'
import { mediaNameFromUrl } from '@/utils/mediaReferences'
import { highlightSearchTerm } from '@/utils/clientSearch'
import { htmlDiff } from '@/utils/htmlDiff'
import type { GetContentBundleResponse, ApiGlossaryTerm } from '@/types/api'

interface RichContentProps {
  html: string
  bundle: GetContentBundleResponse
  /**
   * When true, glossary terms in the HTML are annotated with hover popovers.
   * Defaults to false — only Practice Area and Practice content should enable this.
   */
  enableGlossary?: boolean
  /**
   * Optional shared Set of already-seen glossary term IDs.
   * When provided (and shared across all RichContent instances on a page),
   * each glossary term will only be highlighted on its first occurrence
   * across the entire page rather than once per section.
   *
   * The Set is mutated in-place as terms are matched.
   *
   * If not provided, a local Set is used (each RichContent instance
   * highlights independently).
   */
  seenTermIds?: Set<number>
  /** When set, wraps all occurrences of this term in <mark> tags. */
  highlightTerm?: string
  /**
   * Published (original) HTML for this field. When draft preview is active
   * (appStore.showDraftContent) and this differs from `html`, the rendered content
   * shows an inline diff with additions/deletions marked. Ignored otherwise.
   */
  original?: string | null
}

interface PopoverState {
  termId: number
  term: string
  definition: string
  top: number
  left: number
}

export function RichContent({ html, bundle, enableGlossary: enableGlossaryProp = false, seenTermIds, highlightTerm, original }: RichContentProps) {
  const enableGlossary = enableGlossaryProp && bundle.glossaryHighlightingEnabled !== false
  const { t } = useTranslation()
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const [popover, setPopover] = useState<PopoverState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { setOverlayPage, setPdfViewer, showDraftContent, showContentDiff } = useAppStore()
  const isCompactLayout = useIsCompact()

  // Draft preview: when draft content is shown AND the diff view is enabled, and an original
  // differs, replace the source HTML with a diff (<ins>/<del>) before the glossary/media/search
  // pipeline runs over it. With the diff view off, the draft is rendered in situ (plain html).
  const baseHtml = useMemo(
    () => (showDraftContent && showContentDiff && original != null && original !== html ? htmlDiff(original, html) : html),
    [showDraftContent, showContentDiff, original, html],
  )

  // Local fallback Set used when the caller does not supply a shared one.
  // Stable across renders for this component instance.
  const localSeenTermIds = useRef(new Set<number>())

  // Build a fast lookup map of termId -> term data
  const termMap = useMemo(() => {
    const map = new Map<number, ApiGlossaryTerm>()
    for (const t of bundle.glossaryTerms) map.set(t.id, t)
    return map
  }, [bundle.glossaryTerms])

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => {
      setPopover(null)
      closeTimerRef.current = null
    }, 120)
  }

  // Mouse-enter/-leave on glossary terms to show/hide the fixed popover
  useEffect(() => {
    const container = containerRef.current
    if (!container || !enableGlossary) return

    const onEnter = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>('.glossary-term')
      if (!target) return
      cancelClose()
      const termId = Number(target.dataset.termId)
      const termData = termMap.get(termId)
      if (!termData) return

      const rect = target.getBoundingClientRect()
      // Position popover above the term; if near the top of the viewport, show below
      const POPOVER_HEIGHT = 160 // rough estimate
      const showAbove = rect.top > POPOVER_HEIGHT + 8
      const top = showAbove ? rect.top - 8 : rect.bottom + 8

      setPopover({
        termId,
        term: termData.term,
        definition: (termData.definition ?? '').slice(0, 200),
        top,
        // Clamp both edges: a term near the left edge previously produced a negative offset.
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 300)),
      })
    }

    const onLeave = (e: MouseEvent) => {
      const related = e.relatedTarget as HTMLElement | null
      // Don't schedule close if mouse moved into the popover portal itself
      if (related?.closest?.('.glossary-popover-portal')) return
      scheduleClose()
    }

    container.addEventListener('mouseover', onEnter)
    container.addEventListener('mouseout', onLeave)
    return () => {
      container.removeEventListener('mouseover', onEnter)
      container.removeEventListener('mouseout', onLeave)
      cancelClose()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, termMap])

  // Handle all clicks inside rich content via React event delegation
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'IMG') {
        setLightboxSrc((target as HTMLImageElement).src)
        return
      }

      const link = target.closest('a')
      if (!link) return
      const href = link.getAttribute('href') || ''
      // Offline, cached media links point at blob: URLs that map back to the system file name.
      const systemFileName = mediaNameFromUrl(href) ?? (href.startsWith('blob:') ? resolveOriginalKey(href) : undefined)
      if (!systemFileName) return
      const asset = bundle.media?.find((m) => m.systemFileName === systemFileName)
      const isPdf = asset ? asset.mimeType === 'application/pdf' : systemFileName.toLowerCase().endsWith('.pdf')
      if (!isPdf) return

      e.preventDefault()
      const title = link.textContent?.trim() || t('pdfViewer.fallbackTitle')
      setPdfViewer({ systemFileName, title })
    },
    [bundle.media, setPdfViewer, t],
  )

  // Inject glossary spans — memoized so the expensive string processing only
  // re-runs when the HTML content or glossary term list actually changes.
  // NOTE: seenTermIds is a mutable Set passed by reference; it is intentionally
  // NOT listed as a memo dependency because its identity is stable (same ref)
  // and its contents are mutated in-place by injectGlossarySpans.
  const htmlWithGlossary = useMemo(
    () => enableGlossary
      ? injectGlossarySpans(baseHtml, bundle.glossaryTerms, seenTermIds ?? localSeenTermIds.current)
      : baseHtml,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseHtml, bundle.glossaryTerms, enableGlossary],
  )
  
  // Process media URLs: online resolves to absolute API URLs; offline substitutes blob: URLs
  // from the IndexedDB media cache.
  const withMediaUrls = useOfflineRichContentMediaUrls(htmlWithGlossary, bundle.media)
  const popoverDefinition = useOfflineRichContentMediaUrls(popover?.definition, bundle.media)

  // Apply search term highlighting last so marks appear over glossary-injected spans
  const processedHtml = useMemo(
    () => (highlightTerm ? highlightSearchTerm(withMediaUrls, highlightTerm) : withMediaUrls),
    [withMediaUrls, highlightTerm],
  )

  // Determine whether popover should render above or below the term
  const POPOVER_HEIGHT = 160
  const renderAbove = popover !== null && popover.top > POPOVER_HEIGHT + 8

  return (
    <>
      <div
        ref={containerRef}
        className="rich-content"
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />

      {enableGlossary && popover && createPortal(
        <div
          className={`glossary-popover-portal${isCompactLayout ? ' glossary-popover-portal--sheet' : ''}`}
          style={isCompactLayout
            ? { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 2000 }
            : {
                position: 'fixed',
                top: renderAbove ? undefined : popover.top,
                bottom: renderAbove ? window.innerHeight - popover.top : undefined,
                left: popover.left,
                zIndex: 2000,
              }}
          // Hover-intent: cancel any pending close when mouse enters, schedule close on leave
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {/* Touch has no pointer-leave, so the sheet needs an explicit dismissal. */}
          {isCompactLayout && (
            <button
              type="button"
              className="glossary-popover-close"
              onClick={() => setPopover(null)}
              aria-label={t('common.close')}
            >
              ×
            </button>
          )}
          <strong>{popover.term}</strong>
          <br />
          <span dangerouslySetInnerHTML={{ __html: popoverDefinition }} />
          {(popover.definition.length >= 200) ? '...' : ''}
          <a
            href={`/glossary#term-${popover.termId}`}
            className="read-more"
            onClick={(e) => {
              e.preventDefault()
              setPopover(null)
              setOverlayPage({ type: 'glossary', hash: `#term-${popover.termId}` })
            }}
          >
            {t('glossary.readMore')}
          </a>
        </div>,
        document.body,
      )}

      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </>
  )
}
